import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function HomePage() {
  useEffect(() => {
    // Update SEO metadata to match embed_code
    document.title = 'Henry Adebisi | Finance & Strategy Leader | Founder, AfriStakes';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Finance and strategy leader with a passion for innovation and sustainable growth. Founder of AfriStakes and Co-Founder of Henatos Ventures, connecting Africa\'s real economy to global opportunities from the UK.');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com'}/`);
    }
    
    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute('content', 'finance strategy, fintech Africa, investment frameworks, financial transformation, donor partnerships');
    }

    // Add Schema.org JSON-LD
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Henry Olamide Adebisi",
      "jobTitle": "Finance & Strategy Leader",
      "description": "Finance and strategy leader with a passion for innovation and sustainable growth. Founder of AfriStakes and Co-Founder of Henatos Ventures, connecting Africa's real economy to global opportunities from the UK.",
      "url": import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com',
      "sameAs": [
        "https://www.linkedin.com/in/henry-adebisi/",
        "https://afristakes.com",
        "https://henatosbiz.com"
      ],
      "worksFor": [
        {
          "@type": "Organization",
          "name": "AfriStakes",
          "url": "https://afristakes.com"
        },
        {
          "@type": "Organization",
          "name": "Henatos Ventures",
          "url": "https://henatosbiz.com"
        }
      ],
      "knowsAbout": [
        "Strategic Finance",
        "Fintech Innovation",
        "African Markets",
        "Investment Frameworks",
        "Cross-border Operations"
      ],
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "NG"
      }
    };

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);

    return () => {
      const schemaToRemove = document.querySelector('script[type="application/ld+json"]');
      if (schemaToRemove) {
        schemaToRemove.remove();
      }
    };
  }, []);

  const highlights = [
    {
      title: 'Strategic Finance Leadership',
      description: 'Across 7 African markets (Nigeria, Ghana, Kenya, Uganda, Tanzania, Burundi, and Zambia) & the United Kingdom',
      icon: 'ri-global-line'
    },
    {
      title: 'International Business & Partnerships',
      description: 'Scaled Businesses across East & Southern Africa, Through engagement with Jobberman, lead projects with Mastercard Foundation, UNICEF, USAID & UNDP',
      icon: 'ri-team-line'
    },
    {
      title: 'Thought Leadership',
      description: 'Published insights on African finance, fintech, and SME growth',
      icon: 'ri-lightbulb-line'
    }
  ];

  const ventures = [
    {
      name: 'AfriStakes',
      description: 'Fintech venture developing access to structured capital for Africa\'s real economy',
      url: 'https://afristakes.com',
      icon: 'ri-bank-line'
    },
    {
      name: 'Henatos Ventures',
      description: 'Trade and export operations optimizing supply chains across borders',
      url: 'https://henatosbiz.com',
      icon: 'ri-ship-line'
    }
  ];

  const pressMentions = [
    { name: 'BusinessDay', logo: 'ri-newspaper-line' },
    { name: 'Business Insider Africa', logo: 'ri-global-line' },
    { name: 'News Central', logo: 'ri-tv-line' },
    { name: 'LinkedIn Pulse', logo: 'ri-linkedin-line' }
  ];

  const testimonials = [
    {
      quote: "Henry's leadership in financial strategy and governance has been critical in shaping scalable operations across our digital talent platforms. His forward-looking approach to data-driven finance and impact measurement reflects the kind of innovation that continues to move Africa's tech ecosystem forward.",
      author: "Herbert Pasipamire",
      company: "Chief Commercial Officer, The African Talent Company | Group Chief Financial Officer, ROAM Africa"
    },
    {
      quote: "Henry brought a level of financial and strategic discipline that redefined how we approached growth and sustainability. His leadership helped strengthen Jobberman's profitability, build accountability across teams, and deepen the company's position as a tech-driven enterprise creating real impact across Africa.",
      author: "Oreoluwa Boboye",
      company: "Former Chief Executive Officer, Jobberman Nigeria | Chief Operating Officer, Loubby AI"
    },
    {
      quote: "Henry has a rare ability to bridge financial strategy with innovation and execution. He approaches every challenge with a solutions mindset, balancing structure with agility, which makes him a valuable partner in driving growth and organizational impact. His leadership and strategic clarity consistently elevate the teams and businesses he works with across Africa.",
      author: "Rolake Rosiji",
      company: "Regional Director – Africa, Seedstars"
    }
  ];

  return (
    <div className="min-h-screen bg-off-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section 
          className="py-20 min-h-screen bg-cover bg-center relative flex items-center"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/6b84a11e410079b56c6291a27cbf72c2/643e4afc8cc36133e45cdb4cc05b1ccc.png')`,
            backgroundPosition: 'center 20%'
          }}
        >
          {/* Gradient overlay positioned to not cover the face */}
          <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/70 to-transparent"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Text content positioned on the left */}
              <div className="text-left">
                <h1 className="font-sora font-bold text-4xl lg:text-6xl text-white mb-6 leading-tight">
                  Henry Adebisi
                </h1>
                <p className="text-xl lg:text-2xl text-gold mb-4 font-semibold">
                  Finance & Strategy Leader | Founder of AfriStakes | Building Innovative and Scalable Ventures Across Africa and the UK
                </p>
                <p className="text-gray-200 text-lg mb-8 leading-relaxed max-w-lg">
                  I build financial systems, partnerships, and investment frameworks that help African businesses scale across borders.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button variant="secondary" size="lg" href="/contact">
                    Book Henry
                  </Button>
                  <Button variant="outline" size="lg" href="/writing" className="border-white text-white hover:bg-white hover:text-navy bg-white/30 backdrop-blur-sm font-semibold">
                    Read Articles
                  </Button>
                </div>
              </div>
              
              {/* Right side left empty to show Henry's image */}
              <div className="hidden lg:block">
                {/* This space is intentionally left empty to showcase Henry's image */}
              </div>
            </div>
          </div>
        </section>

        {/* Highlights Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-off-white rounded-lg p-8 text-center hover:shadow-lg transition-shadow duration-300">
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className={`${highlight.icon} text-3xl text-gold`}></i>
                  </div>
                  <h3 className="font-sora font-bold text-xl text-navy mb-4">
                    {highlight.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {highlight.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Ventures */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-sora font-bold text-3xl lg:text-4xl text-navy mb-4">
                Featured Ventures
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Building innovative solutions that bridge African businesses with global opportunities
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {ventures.map((venture, index) => (
                <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${venture.icon} text-2xl text-gold`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-sora font-bold text-xl text-navy mb-3">
                        {venture.name}
                      </h3>
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {venture.description}
                      </p>
                      <a 
                        href={venture.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gold hover:text-navy font-semibold transition-colors duration-200"
                      >
                        Learn More <i className="ri-external-link-line ml-1"></i>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Press Mentions */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-sora font-bold text-2xl text-navy mb-4">
                Featured In
              </h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {pressMentions.map((mention, index) => (
                <div key={index} className="flex flex-col items-center p-6 bg-gray-50 rounded-lg">
                  <i className={`${mention.logo} text-3xl text-gray-400 mb-3`}></i>
                  <span className="text-gray-600 font-medium text-center">
                    {mention.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-sora font-bold text-3xl lg:text-4xl text-navy mb-4">
                What People Say
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Testimonials from partners, colleagues, and industry leaders
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white rounded-lg p-8 shadow-lg">
                  <div className="mb-6">
                    <i className="ri-double-quotes-l text-3xl text-gold"></i>
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic">
                    {testimonial.quote}
                  </p>
                  <div>
                    <p className="font-semibold text-navy">
                      {testimonial.author}
                    </p>
                    <p className="text-gray-500 text-sm">
                      {testimonial.company}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-navy">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-sora font-bold text-3xl lg:text-4xl text-white mb-6">
              Ready to Transform Your Financial Strategy?
            </h2>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed">
              Partner with Henry to build sustainable financial systems and unlock growth opportunities across African markets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="lg" href="/contact">
                Start a Conversation
              </Button>
              <Button variant="outline" size="lg" href="/about" className="border-white text-white hover:bg-white hover:text-navy bg-white/20 backdrop-blur-sm font-semibold">
                Learn More About Henry
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
